import Color from '@tiptap/extension-color';
import Dropcursor from '@tiptap/extension-dropcursor';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import SubScript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { BubbleMenu, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useRef } from 'react';

import { Box, Button, FileButton, Flex, Group, ScrollArea, Text, TextInput } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { Link, RichTextEditor } from '@mantine/tiptap';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { referencesApi } from '@/shared/api/references';
import { IReference } from '@/shared/types/reference.types';

import { IconPhoto, IconX } from '@tabler/icons-react';

import './styles.css';

type ReferencesEditor = {
	reference: IReference | null;
	setReference: React.Dispatch<React.SetStateAction<IReference | null>>;
	setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
};

const ReferencesEditor: React.FC<ReferencesEditor> = ({ reference, setReference, setIsEdit }) => {
	const queryClient = useQueryClient();

	const fileInputResetRef = useRef<() => void>(null);

	const editor = useEditor(
		{
			extensions: [
				StarterKit,
				Underline,
				Link.configure({
					openOnClick: true,
				}),
				Superscript,
				SubScript,
				Highlight,
				TextAlign.configure({ types: ['heading', 'paragraph'] }),
				Color,
				TextStyle,
				Image.configure({
					inline: true,
					allowBase64: true,
				}),
				Dropcursor,
			],
			content: reference?.content ?? '',
		},
		[reference],
	);

	const form = useForm({
		initialValues: {
			name: reference?.name ?? '',
			description: reference?.description ?? '',
		},
		validate: {
			name: isNotEmpty(),
			description: isNotEmpty(),
		},
	});

	const { mutate: updateReference, isPending: isUpdatingReference } = useMutation({
		mutationFn: referencesApi.updateReference,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['references'] });
			handleClose();
		},
		onError: () => {
			notifications.show({
				message: 'Не удалось обновить справку',
				color: 'red',
				icon: <IconX />,
			});
		},
	});

	const { mutate: createReference, isPending: isAddingReference } = useMutation({
		mutationFn: referencesApi.createReference,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['references'] });
			handleClose();
		},
		onError: () => {
			notifications.show({
				message: 'Не удалось создать справку',
				color: 'red',
				icon: <IconX />,
			});
		},
	});

	const handleFileChange = (file: File | null) => {
		fileInputResetRef.current?.();

		const reader = new FileReader();

		reader.onload = () => {
			editor
				?.chain()
				.focus()
				.setImage({
					src: reader.result?.toString() ?? '',
				})
				.run();
		};

		if (file) reader.readAsDataURL(file);
	};

	const handleClose = () => {
		setIsEdit(false);
		setReference(null);
	};

	const handleSumbit = () => {
		form.validate();
		if (!form.isValid()) return;

		if (reference) {
			updateReference({
				id: reference.id,
				payload: {
					id: reference.id,
					content: editor?.getHTML() ?? '',
					name: form.values.name,
					description: form.values.description,
				},
			});
		} else {
			createReference({
				content: editor?.getHTML() ?? '',
				name: form.values.name,
				description: form.values.description,
			});
		}
	};

	return (
		<Box
			pos="relative"
			style={{ display: 'flex', flexDirection: 'column', gap: '10px', overflow: 'hidden' }}
		>
			<Flex wrap={'wrap'} gap={15}>
				<TextInput
					placeholder="Справка"
					w={250}
					label="Название справки"
					withAsterisk
					{...form.getInputProps('name')}
				/>
				<TextInput
					placeholder="Описание"
					w={250}
					label="Описание справки"
					withAsterisk
					{...form.getInputProps('description')}
				/>
			</Flex>
			<ScrollArea h={'100dvh'} pr={'md'} scrollbarSize={12} offsetScrollbars>
				<RichTextEditor editor={editor}>
					<RichTextEditor.Toolbar sticky>
						<RichTextEditor.ControlsGroup>
							<RichTextEditor.Bold />
							<RichTextEditor.Italic />
							<RichTextEditor.Underline />
							<RichTextEditor.Strikethrough />
							<RichTextEditor.ClearFormatting />
							<RichTextEditor.Highlight />
							<RichTextEditor.Code />
						</RichTextEditor.ControlsGroup>

						<RichTextEditor.ControlsGroup>
							<RichTextEditor.ColorPicker
								colors={[
									'#25262b',
									'#868e96',
									'#fa5252',
									'#e64980',
									'#be4bdb',
									'#7950f2',
									'#4c6ef5',
									'#228be6',
									'#15aabf',
									'#12b886',
									'#40c057',
									'#82c91e',
									'#fab005',
									'#fd7e14',
								]}
							/>
							<RichTextEditor.UnsetColor />
						</RichTextEditor.ControlsGroup>

						<RichTextEditor.ControlsGroup>
							<RichTextEditor.H1 />
							<RichTextEditor.H2 />
							<RichTextEditor.H3 />
							<RichTextEditor.H4 />
						</RichTextEditor.ControlsGroup>

						<RichTextEditor.ControlsGroup>
							<RichTextEditor.Blockquote />
							<RichTextEditor.Hr />
							<RichTextEditor.BulletList />
							<RichTextEditor.OrderedList />
							<RichTextEditor.Subscript />
							<RichTextEditor.Superscript />
						</RichTextEditor.ControlsGroup>

						<RichTextEditor.ControlsGroup>
							<RichTextEditor.Link />
							<RichTextEditor.Unlink />
						</RichTextEditor.ControlsGroup>

						<RichTextEditor.ControlsGroup>
							<FileButton
								resetRef={fileInputResetRef}
								onChange={handleFileChange}
								accept="image/png,image/jpeg"
							>
								{(props) => (
									<Button
										{...props}
										w={26}
										h={26}
										variant={'outline'}
										p={0}
										radius={'md'}
										style={{ borderColor: '#D9DEE2' }}
									>
										<IconPhoto height={16} color="#525252" />
									</Button>
								)}
							</FileButton>
						</RichTextEditor.ControlsGroup>

						<RichTextEditor.ControlsGroup>
							<RichTextEditor.AlignLeft />
							<RichTextEditor.AlignCenter />
							<RichTextEditor.AlignJustify />
							<RichTextEditor.AlignRight />
						</RichTextEditor.ControlsGroup>

						<RichTextEditor.ControlsGroup>
							<RichTextEditor.Undo />
							<RichTextEditor.Redo />
						</RichTextEditor.ControlsGroup>
					</RichTextEditor.Toolbar>

					<BubbleMenu editor={editor}>
						<RichTextEditor.ControlsGroup>
							<RichTextEditor.Bold />
							<RichTextEditor.Italic />
							<RichTextEditor.Underline />
							<RichTextEditor.Strikethrough />
							<RichTextEditor.ClearFormatting />
							<RichTextEditor.Highlight />
							<RichTextEditor.Link />
						</RichTextEditor.ControlsGroup>
					</BubbleMenu>

					<RichTextEditor.Content />
				</RichTextEditor>
			</ScrollArea>
			<Group>
				<Button
					variant="filled"
					radius="lg"
					onClick={handleSumbit}
					loading={isUpdatingReference || isAddingReference}
				>
					<Text fw={500}>Сохранить</Text>
				</Button>
				<Button
					variant="filled"
					radius="lg"
					bg={'red'}
					onClick={handleClose}
					loading={isUpdatingReference || isAddingReference}
				>
					<Text fw={500}>Отмена</Text>
				</Button>
			</Group>
		</Box>
	);
};

export default ReferencesEditor;
